import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mic2, Sparkles, Repeat, SlidersHorizontal, Play } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AccountMenu } from "@/components/AccountMenu";
import { PricingSection } from "@/components/PricingSection";

const BG = "https://images.unsplash.com/photo-1562362898-d1a9d124fd77?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

const FEATURES = [
  { icon: Mic2, title: "Record & import", text: "Sing over your own songs with count-in, metronome and punch-in." },
  { icon: Sparkles, title: "Harmonies in key", text: "Natural, formant-preserving voices that follow your scale note by note." },
  { icon: Repeat, title: "Loop takes", text: "Loop a phrase and capture a fresh take every pass." },
  { icon: SlidersHorizontal, title: "Candlelit mixer", text: "EQ, reverb, delay, pan and export to WAV." },
];

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [demos, setDemos] = useState([]);

  useEffect(() => {
    api.listDemos().then(setDemos).catch(() => {});
    if (window.location.hash === "#pricing") setTimeout(() => document.getElementById("pricing")?.scrollIntoView(), 300);
  }, []);

  return (
    <div className="min-h-screen bg-[#080708]" data-testid="landing-page">
      <header className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 h-20 flex items-center gap-6">
        <span className="font-gothic text-lg tracking-[0.15em] text-amber-100/90">VOX <span className="text-[#c92a3e]">✦</span> NOCTURNA</span>
        <span className="flex-1" />
        <a href="#pricing" data-testid="nav-pricing-link" className="eyebrow hover:text-amber-200 transition-colors">Pricing</a>
        {user && <Link to="/library" data-testid="nav-library-link" className="eyebrow hover:text-amber-200 transition-colors">My crypt</Link>}
        <AccountMenu />
      </header>

      <section className="relative overflow-hidden -mt-20 pt-20">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#080708]/60 via-[#080708]/85 to-[#080708]" />
        <div className="relative max-w-6xl mx-auto px-6 sm:px-10 py-24 rise">
          <p className="eyebrow text-[#D99B43]">✦ Vocal harmony sanctum ✦</p>
          <h1 className="font-gothic text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-amber-100/90 mt-5 leading-[1.05] max-w-3xl">
            Raise one voice <span className="text-[#c92a3e]">into a choir</span>
          </h1>
          <p className="font-cormorant italic text-xl text-stone-300 mt-6 max-w-xl leading-relaxed">
            Record or import your vocal, summon harmonies by interval or by AI, and mix them in candlelit reverb. Free to start. Pro for $9 a month or $90 a year, with a 7-day free trial.
          </p>
          <div className="flex flex-wrap gap-4 mt-10">
            <button data-testid="hero-start-btn" onClick={() => nav(user ? "/library" : "/login")} className="btn-crimson">
              {user ? "Open my crypt" : "Start free"}
            </button>
            <a href="#pricing" data-testid="hero-pricing-btn" className="btn-gothic">See pricing · 7 days free</a>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 sm:px-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {FEATURES.map((f, i) => (
          <div key={f.title} className="stone-panel p-6 rise" style={{ animationDelay: `${0.08 * i}s` }}>
            <f.icon size={22} className="text-[#c92a3e]" />
            <h3 className="font-gothic uppercase tracking-wider text-amber-100/90 mt-4">{f.title}</h3>
            <p className="text-sm text-stone-400 mt-2 leading-relaxed">{f.text}</p>
          </div>
        ))}
      </section>

      <PricingSection />

      {demos.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 sm:px-10 pb-24" data-testid="public-demos-section">
          <div className="flex items-center gap-3 mb-6">
            <h2 className="font-gothic text-lg uppercase tracking-[0.2em] text-stone-200">Public demo hymns</h2>
            <span className="flex-1 h-px bg-[#352d40]" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {demos.slice(0, 9).map((p) => (
              <button key={p.id} data-testid="public-demo-card" onClick={() => nav(`/studio/${p.id}`)} className="stone-panel p-4 text-left flex items-center gap-3 hover:border-[#8B1E2B] transition-colors">
                <Play size={16} className="text-[#c92a3e] shrink-0" />
                <span className="min-w-0">
                  <span className="block font-gothic tracking-wider text-amber-100/90 truncate">{p.name}</span>
                  <span className="eyebrow">{p.key} {p.scale} · {p.tracks.length} voices</span>
                </span>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
